/**
 * API 请求封装模块
 */

/** API 基础 URL */
const BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

/** 默认超时时间（毫秒） */
const DEFAULT_TIMEOUT = 30000

/** 存储的认证 token */
let authToken: string | null = null

/** 401 响应回调（用于触发自动登出） */
let onUnauthorized: (() => void) | null = null

/**
 * 设置 401 未授权响应的回调函数
 *
 * @param callback - 收到 401 响应时执行的回调
 */
export function setUnauthorizedCallback(callback: () => void): void {
  onUnauthorized = callback
}

/**
 * 设置认证 token
 */
export function setAuthToken(token: string): void {
  authToken = token
}

/**
 * 清除认证 token
 */
export function clearAuthToken(): void {
  authToken = null
}

/**
 * 获取当前 token
 */
export function getAuthToken(): string | null {
  return authToken
}

/**
 * 请求选项
 */
export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  data?: unknown
  headers?: Record<string, string>
  timeout?: number
  requireAuth?: boolean
}

/**
 * 发起 API 请求
 *
 * @param url - 请求路径（相对路径会自动拼接 BASE_URL）
 * @param options - 请求配置
 * @returns Promise<T> - 响应数据
 */
export async function request<T>(url: string, options: RequestOptions = {}): Promise<T> {
  const {
    method = 'GET',
    data,
    timeout = DEFAULT_TIMEOUT,
    requireAuth = true,
    headers = {},
  } = options

  // 构建完整 URL
  const fullUrl = url.startsWith('http') ? url : `${BASE_URL}${url}`

  // 构建请求头
  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...headers,
  }

  // 如果需要认证且有 token，添加 Authorization 头
  if (requireAuth && authToken) {
    requestHeaders.Authorization = `Bearer ${authToken}`
  }

  // 构建 fetch 选项
  const fetchOptions: RequestInit = {
    method,
    headers: requestHeaders,
    signal: AbortSignal.timeout(timeout),
  }

  // 添加请求体
  if (data && method !== 'GET') {
    fetchOptions.body = JSON.stringify(data)
  }

  const response = await fetch(fullUrl, fetchOptions)

  if (!response.ok) {
    // 处理 401 未授权响应 - 触发自动登出
    if (response.status === 401 && onUnauthorized) {
      onUnauthorized()
    }

    const errorData = await response.json().catch(() => ({}))
    const errorMessage = extractErrorMessage(errorData, response.status)
    throw new Error(errorMessage)
  }

  return response.json()
}

/**
 * 从响应数据中提取错误信息
 */
function extractErrorMessage(data: unknown, statusCode: number): string {
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>
    if (typeof obj.detail === 'string') {
      return obj.detail
    }
    if (typeof obj.message === 'string') {
      return obj.message
    }
    if (typeof obj.error === 'string') {
      return obj.error
    }
  }

  switch (statusCode) {
    case 400:
      return '请求参数错误'
    case 401:
      return '认证失败，请重新登录'
    case 403:
      return '没有访问权限'
    case 404:
      return '请求的资源不存在'
    case 429:
      return '请求过于频繁，请稍后再试'
    case 500:
      return '服务器内部错误'
    case 502:
    case 503:
    case 504:
      return '服务暂时不可用，请稍后再试'
    default:
      return `请求失败 (${statusCode})`
  }
}
